// Modal de gestion del catalogo de tags del partner (`partner_tags`, 0034).
//
// Es el "editar los tags existentes" del selector: alta, renombrado del slug,
// nombre visible, descripcion, color, archivar y borrar. Se abre desde el
// propio selector del plan (PlanTagsEditor) para que el admin no tenga que
// salir a otra pantalla a definir un tag que necesita justo ahora.
//
// Dos operaciones tienen consecuencias fuera de este modal y por eso piden
// confirmacion aparte:
//   - RENOMBRAR el slug: es lo que la OTT recibe en /users/{id}/entitlements.
//     Propaga solo a todos los planes (ON UPDATE CASCADE) pero rompe a
//     cualquier integrador que lo tenga hardcodeado.
//   - BORRAR: el backend lo rechaza con 409 si algun plan lo usa. Para sacarlo
//     de circulacion sin tocar accesos existentes esta "archivar".
//
// A11y: <dialog> nativo (focus-trap del browser), igual que ClientOverridesModal.

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, X, Pencil, Archive, ArchiveRestore } from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';
import {
  listPartnerTags,
  createPartnerTag,
  patchPartnerTag,
  deletePartnerTag,
  AdminApiError,
  type PartnerTag,
} from '@/lib/api';
import { fieldCls } from '@/lib/form-styles';

const SLUG_RE = /^[a-z0-9_-]+$/;
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

// Paleta acotada en vez de un color picker libre: los chips tienen que seguir
// leyendose sobre fondo claro y oscuro, y un hex arbitrario no lo garantiza.
const PALETTE = [
  '#2563eb',
  '#7c3aed',
  '#db2777',
  '#dc2626',
  '#ea580c',
  '#ca8a04',
  '#16a34a',
  '#0891b2',
  '#475569',
];

/** Chip de un tag. Exportado: el selector pinta exactamente lo mismo. */
export function TagChip({
  tag,
  color,
  muted = false,
}: {
  tag: string;
  color: string | null;
  muted?: boolean;
}): JSX.Element {
  const c = color && HEX_RE.test(color) ? color : null;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${
        muted ? 'opacity-50' : ''
      } ${c ? '' : 'bg-ink-muted/15 text-ink dark:text-ink-inverted'}`}
      style={c ? { backgroundColor: `${c}22`, color: c } : undefined}
    >
      <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current" />
      {tag}
    </span>
  );
}

interface DraftTag {
  tag: string;
  display_name: string;
  description: string;
  color: string | null;
}

const EMPTY_DRAFT: DraftTag = { tag: '', display_name: '', description: '', color: PALETTE[0] ?? null };

export default function TagManagerModal({
  open,
  partnerId,
  canWrite,
  onClose,
}: {
  open: boolean;
  partnerId: string;
  canWrite: boolean;
  onClose: () => void;
}): JSX.Element {
  const ref = useRef<HTMLDialogElement>(null);
  const qc = useQueryClient();

  const [draft, setDraft] = useState<DraftTag>(EMPTY_DRAFT);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<DraftTag>(EMPTY_DRAFT);
  const [confirmDelete, setConfirmDelete] = useState<PartnerTag | null>(null);
  const [confirmRename, setConfirmRename] = useState<{ from: string; to: string } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    if (open && !dlg.open) dlg.showModal();
    if (!open && dlg.open) dlg.close();
  }, [open]);

  useEffect(() => {
    if (open) {
      setDraft(EMPTY_DRAFT);
      setCreating(false);
      setEditing(null);
      setActionError(null);
    }
  }, [open]);

  const tagsQ = useQuery({
    queryKey: ['admin-partner-tags', partnerId],
    queryFn: () => listPartnerTags(partnerId),
    enabled: open,
  });

  const invalidate = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['admin-partner-tags', partnerId] });
    // El selector del plan muestra display_name/color del catalogo: si cambian
    // aca, sus chips quedan viejos hasta refetchear.
    await qc.invalidateQueries({ queryKey: ['admin-plan-tags'] });
  };

  const describeError = (err: unknown): string =>
    err instanceof AdminApiError ? err.message : 'No se pudo completar la operación.';

  const create = useMutation({
    mutationFn: () =>
      createPartnerTag(partnerId, {
        tag: draft.tag.trim(),
        display_name: draft.display_name.trim(),
        description: draft.description.trim() || null,
        color: draft.color,
      }),
    onSuccess: async () => {
      await invalidate();
      setDraft(EMPTY_DRAFT);
      setCreating(false);
      setActionError(null);
    },
    onError: (err) => setActionError(describeError(err)),
  });

  const patch = useMutation({
    mutationFn: (vars: { tag: string; body: Parameters<typeof patchPartnerTag>[2] }) =>
      patchPartnerTag(partnerId, vars.tag, vars.body),
    onSuccess: async () => {
      await invalidate();
      setEditing(null);
      setConfirmRename(null);
      setActionError(null);
    },
    onError: (err) => setActionError(describeError(err)),
  });

  const remove = useMutation({
    mutationFn: (tag: string) => deletePartnerTag(partnerId, tag),
    onSuccess: async () => {
      await invalidate();
      setConfirmDelete(null);
      setActionError(null);
    },
    onError: (err) => {
      // tag_in_use trae los planes afectados en el mensaje del backend: es
      // accionable tal cual, no lo reemplazamos por un texto generico.
      setActionError(describeError(err));
      setConfirmDelete(null);
    },
  });

  const startEdit = (t: PartnerTag): void => {
    setEditing(t.tag);
    setEditDraft({
      tag: t.tag,
      display_name: t.display_name,
      description: t.description ?? '',
      color: t.color,
    });
    setActionError(null);
  };

  const submitEdit = (original: PartnerTag): void => {
    const body: Parameters<typeof patchPartnerTag>[2] = {};
    if (editDraft.display_name.trim() !== original.display_name) {
      body.display_name = editDraft.display_name.trim();
    }
    if ((editDraft.description.trim() || null) !== original.description) {
      body.description = editDraft.description.trim() || null;
    }
    if (editDraft.color !== original.color) body.color = editDraft.color;

    const renamed = editDraft.tag.trim() !== original.tag;
    if (renamed) {
      // El rename se confirma aparte porque es breaking para la OTT. El resto
      // del patch viaja junto una vez confirmado.
      setConfirmRename({ from: original.tag, to: editDraft.tag.trim() });
      return;
    }
    if (Object.keys(body).length === 0) {
      setEditing(null);
      return;
    }
    patch.mutate({ tag: original.tag, body });
  };

  const draftSlugValid = SLUG_RE.test(draft.tag.trim()) && draft.tag.trim().length <= 40;
  const editSlugValid = SLUG_RE.test(editDraft.tag.trim()) && editDraft.tag.trim().length <= 40;

  return (
    <>
      <dialog
        ref={ref}
        aria-labelledby="tagmgr-title"
        onClose={onClose}
        className="w-full max-w-2xl rounded-xl bg-surface p-0 text-ink backdrop:bg-black/50 dark:bg-surface-dark dark:text-ink-inverted"
      >
        <div className="flex items-center justify-between border-b border-surface-border px-6 py-4 dark:border-surface-dark-border">
          <div>
            <h2 id="tagmgr-title" className="text-lg font-semibold">
              Tags de entitlements
            </h2>
            <p className="text-xs text-ink-muted">
              Catálogo de <span className="font-mono">{partnerId}</span>. El slug es lo que la
              OTT recibe en <span className="font-mono">/users/&#123;id&#125;/entitlements</span>.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="rounded p-1.5 text-ink-muted hover:bg-ink-muted/10"
          >
            <X aria-hidden className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[60vh] space-y-3 overflow-y-auto px-6 py-4">
          {tagsQ.isLoading ? <p className="text-sm text-ink-muted">Cargando…</p> : null}
          {tagsQ.isError ? (
            <p className="text-sm text-status-error">No se pudo cargar el catálogo.</p>
          ) : null}

          {actionError ? (
            <p className="rounded border border-status-error/40 bg-status-error/10 px-3 py-2 text-sm text-status-error">
              {actionError}
            </p>
          ) : null}

          {tagsQ.data?.length === 0 && !creating ? (
            <p className="text-sm text-ink-muted">
              Todavía no hay tags. Creá el primero para poder asignarlo a un plan.
            </p>
          ) : null}

          <ul className="space-y-2">
            {tagsQ.data?.map((t) => (
              <li
                key={t.tag}
                className="rounded-lg border border-surface-border p-3 dark:border-surface-dark-border"
              >
                {editing === t.tag ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                      <label className="block">
                        <span className="mb-1 block text-xs font-medium text-ink-muted">
                          Slug (lo ve la OTT)
                        </span>
                        <input
                          value={editDraft.tag}
                          onChange={(e) => setEditDraft({ ...editDraft, tag: e.target.value })}
                          className={fieldCls({ mono: true })}
                        />
                        {!editSlugValid ? (
                          <p className="mt-1 text-xs text-status-error">
                            Solo minúsculas, números, guion y guion bajo.
                          </p>
                        ) : null}
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-xs font-medium text-ink-muted">
                          Nombre visible (interno)
                        </span>
                        <input
                          value={editDraft.display_name}
                          onChange={(e) =>
                            setEditDraft({ ...editDraft, display_name: e.target.value })
                          }
                          className={fieldCls()}
                        />
                      </label>
                    </div>
                    <label className="block">
                      <span className="mb-1 block text-xs font-medium text-ink-muted">
                        Descripción
                      </span>
                      <input
                        value={editDraft.description}
                        onChange={(e) =>
                          setEditDraft({ ...editDraft, description: e.target.value })
                        }
                        maxLength={300}
                        className={fieldCls()}
                      />
                    </label>
                    <ColorPicker
                      value={editDraft.color}
                      onChange={(color) => setEditDraft({ ...editDraft, color })}
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setEditing(null)}
                        className="min-h-[2.25rem] rounded border border-surface-border px-3 text-sm dark:border-surface-dark-border"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={() => submitEdit(t)}
                        disabled={
                          !editSlugValid ||
                          editDraft.display_name.trim() === '' ||
                          patch.isPending
                        }
                        className="min-h-[2.25rem] rounded bg-brand px-4 text-sm font-medium text-white hover:bg-brand-hover disabled:opacity-50"
                      >
                        {patch.isPending ? 'Guardando…' : 'Guardar'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="min-w-[12rem] flex-1">
                      <div className="flex items-center gap-2">
                        <TagChip
                          tag={t.tag}
                          color={t.color}
                          muted={t.status === 'archived'}
                        />
                        <span className="text-sm">{t.display_name}</span>
                        {t.status === 'archived' ? (
                          <span className="rounded bg-ink-muted/20 px-1.5 py-0.5 text-xs text-ink-muted">
                            archivado
                          </span>
                        ) : null}
                      </div>
                      {t.description ? (
                        <p className="mt-1 text-xs text-ink-muted">{t.description}</p>
                      ) : null}
                      <p className="mt-1 text-xs text-ink-muted">
                        {t.plan_count === 0
                          ? 'sin planes asignados'
                          : `${t.plan_count} plan${t.plan_count === 1 ? '' : 'es'}`}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => startEdit(t)}
                      disabled={!canWrite}
                      className="inline-flex items-center gap-1 text-sm text-brand hover:underline disabled:opacity-50"
                    >
                      <Pencil aria-hidden className="h-3.5 w-3.5" />
                      Editar
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        patch.mutate({
                          tag: t.tag,
                          body: { status: t.status === 'active' ? 'archived' : 'active' },
                        })
                      }
                      disabled={!canWrite || patch.isPending}
                      className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink disabled:opacity-50 dark:hover:text-ink-inverted"
                    >
                      {t.status === 'active' ? (
                        <>
                          <Archive aria-hidden className="h-3.5 w-3.5" />
                          Archivar
                        </>
                      ) : (
                        <>
                          <ArchiveRestore aria-hidden className="h-3.5 w-3.5" />
                          Desarchivar
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(t)}
                      disabled={!canWrite || t.plan_count > 0}
                      title={
                        t.plan_count > 0
                          ? 'Está asignado a algún plan: quitalo de esos planes o archivalo.'
                          : undefined
                      }
                      className="inline-flex items-center gap-1 text-sm text-status-error hover:underline disabled:opacity-40"
                    >
                      <Trash2 aria-hidden className="h-3.5 w-3.5" />
                      Borrar
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>

          {creating ? (
            <div className="space-y-2 rounded-lg border border-surface-border bg-surface-muted p-3 dark:border-surface-dark-border dark:bg-surface-dark-muted">
              <h3 className="text-sm font-medium">Nuevo tag</h3>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-ink-muted">
                    Slug (lo ve la OTT)
                  </span>
                  <input
                    value={draft.tag}
                    onChange={(e) => setDraft({ ...draft, tag: e.target.value })}
                    placeholder="4k"
                    className={fieldCls({ mono: true })}
                  />
                  {draft.tag.trim() !== '' && !draftSlugValid ? (
                    <p className="mt-1 text-xs text-status-error">
                      Solo minúsculas, números, guion y guion bajo.
                    </p>
                  ) : null}
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-ink-muted">
                    Nombre visible (interno)
                  </span>
                  <input
                    value={draft.display_name}
                    onChange={(e) => setDraft({ ...draft, display_name: e.target.value })}
                    placeholder="Calidad 4K"
                    className={fieldCls()}
                  />
                </label>
              </div>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-ink-muted">
                  Descripción (opcional)
                </span>
                <input
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  maxLength={300}
                  placeholder="Habilita el stream en 2160p"
                  className={fieldCls()}
                />
              </label>
              <ColorPicker
                value={draft.color}
                onChange={(color) => setDraft({ ...draft, color })}
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setCreating(false);
                    setDraft(EMPTY_DRAFT);
                  }}
                  className="min-h-[2.25rem] rounded border border-surface-border px-3 text-sm dark:border-surface-dark-border"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => create.mutate()}
                  disabled={
                    !draftSlugValid || draft.display_name.trim() === '' || create.isPending
                  }
                  className="min-h-[2.25rem] rounded bg-brand px-4 text-sm font-medium text-white hover:bg-brand-hover disabled:opacity-50"
                >
                  {create.isPending ? 'Creando…' : 'Crear tag'}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setCreating(true);
                setActionError(null);
              }}
              disabled={!canWrite}
              className="inline-flex min-h-[2.25rem] items-center gap-1.5 rounded border border-surface-border px-3 text-sm disabled:opacity-50 dark:border-surface-dark-border"
            >
              <Plus aria-hidden className="h-4 w-4" />
              Nuevo tag
            </button>
          )}
        </div>

        <div className="flex justify-end border-t border-surface-border px-6 py-3 dark:border-surface-dark-border">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[2.5rem] rounded-lg border border-surface-border px-4 text-sm dark:border-surface-dark-border"
          >
            Cerrar
          </button>
        </div>
      </dialog>

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Borrar tag"
        description={
          confirmDelete ? (
            <p>
              Se borra <strong>{confirmDelete.display_name}</strong> (
              <span className="font-mono">{confirmDelete.tag}</span>) del catálogo. No está
              asignado a ningún plan, así que no cambia el acceso de nadie.
            </p>
          ) : null
        }
        confirmLabel="Borrar"
        danger
        loading={remove.isPending}
        onConfirm={() => confirmDelete && remove.mutate(confirmDelete.tag)}
        onCancel={() => setConfirmDelete(null)}
      />

      <ConfirmDialog
        open={confirmRename !== null}
        title="Renombrar el slug del tag"
        description={
          confirmRename ? (
            <div className="space-y-2">
              <p>
                El slug pasa de <span className="font-mono">{confirmRename.from}</span> a{' '}
                <span className="font-mono">{confirmRename.to}</span> en todos los planes que
                lo usan.
              </p>
              <p className="text-status-error">
                Es lo que la OTT recibe en <span className="font-mono">entitlements</span>. Si
                lo tiene hardcodeado, deja de matchear hasta que la OTT se actualice.
                Coordinalo antes.
              </p>
            </div>
          ) : null
        }
        confirmLabel="Renombrar"
        danger
        loading={patch.isPending}
        onConfirm={() => {
          if (!confirmRename) return;
          patch.mutate({
            tag: confirmRename.from,
            body: {
              tag: confirmRename.to,
              display_name: editDraft.display_name.trim(),
              description: editDraft.description.trim() || null,
              color: editDraft.color,
            },
          });
        }}
        onCancel={() => setConfirmRename(null)}
      />
    </>
  );
}

function ColorPicker({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (color: string | null) => void;
}): JSX.Element {
  return (
    <div>
      <span className="mb-1 block text-xs font-medium text-ink-muted">Color del chip</span>
      <div className="flex flex-wrap items-center gap-1.5">
        {PALETTE.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => onChange(c)}
            aria-label={`Color ${c}`}
            aria-pressed={value === c}
            className={`h-6 w-6 rounded-full border-2 ${
              value === c ? 'border-ink dark:border-ink-inverted' : 'border-transparent'
            }`}
            style={{ backgroundColor: c }}
          />
        ))}
        <button
          type="button"
          onClick={() => onChange(null)}
          aria-pressed={value === null}
          className={`min-h-[1.5rem] rounded border px-2 text-xs ${
            value === null
              ? 'border-ink dark:border-ink-inverted'
              : 'border-surface-border dark:border-surface-dark-border'
          }`}
        >
          sin color
        </button>
      </div>
    </div>
  );
}
